import { useEffect, useMemo, useState } from "react";
import { Alert, Card, Radio, Spin, Table, Tabs, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EnvironmentOutlined, TeamOutlined } from "@ant-design/icons";
import {
  fetchCashflow30dStats,
  fetchChannelCountryStats,
  fetchCountryStats,
  type Cashflow30dStats,
  type ChannelCountryStats,
  type CountryStats,
  type PeriodMetricRow,
} from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import CashflowLineChart from "@/components/CashflowLineChart";
import { countryLabel } from "@/utils/countryLabel";

type ChannelRow = PeriodMetricRow & {
  key: string;
  index: number;
  channelId: string;
  country: string;
};

type MerchantCountryRow = PeriodMetricRow & {
  key: string;
  index: number;
  merchantName: string;
  merchantId: string;
  country: string;
};

const metricColumns: ColumnsType<PeriodMetricRow> = [
  {
    title: "上月总额 (USD)",
    dataIndex: "lastMonthTotalUsd",
    align: "right",
  },
  {
    title: "上月到上月昨日总额",
    dataIndex: "lastMonthToYesterdayUsd",
    align: "right",
  },
  {
    title: "本月到昨日总额",
    dataIndex: "mtdToYesterdayUsd",
    align: "right",
  },
  {
    title: "两月到昨日差比",
    dataIndex: "momDiffRatio",
    align: "right",
  },
  {
    title: "预计两月差比",
    dataIndex: "projectedMomDiffRatio",
    align: "right",
  },
];

function asColumns<T extends PeriodMetricRow>(cols: ColumnsType<PeriodMetricRow>): ColumnsType<T> {
  return cols as ColumnsType<T>;
}

function emptyMetrics(): PeriodMetricRow {
  return {
    lastMonthTotalUsd: "0.00",
    lastMonthToYesterdayUsd: "0.00",
    mtdToYesterdayUsd: "0.00",
    momDiffRatio: "0.00",
    projectedMomDiffRatio: "0.00",
  };
}

export default function HomePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelStats, setChannelStats] = useState<ChannelCountryStats | null>(null);
  const [countryStats, setCountryStats] = useState<CountryStats | null>(null);
  const [cashflow, setCashflow] = useState<Cashflow30dStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [channel, country, flow] = await Promise.all([
          fetchChannelCountryStats(),
          fetchCountryStats(),
          fetchCashflow30dStats(),
        ]);
        if (cancelled) return;
        setChannelStats(channel);
        setCountryStats(country);
        setCashflow(flow);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "加载统计失败");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const merchantName = user?.displayName || user?.account || "—";
  const merchantId = user?.paymentMerchantId || "—";

  const channelRows: ChannelRow[] = useMemo(() => {
    return (channelStats?.rows ?? []).map((r, i) => ({
      ...r,
      key: `${r.channelId}-${r.country}-${i}`,
      index: i + 1,
    }));
  }, [channelStats]);

  const countryRows: MerchantCountryRow[] = useMemo(() => {
    return (countryStats?.rows ?? []).map((r, i) => ({
      ...r,
      key: `${r.country}-${i}`,
      index: i + 1,
      merchantName,
      merchantId,
    }));
  }, [countryStats, merchantName, merchantId]);

  const channelColumns: ColumnsType<ChannelRow> = [
    { title: "序号", dataIndex: "index", width: 64, align: "center" },
    { title: "金流渠道", dataIndex: "channelId", ellipsis: true },
    {
      title: "国家/地区",
      dataIndex: "country",
      render: (v: string) => countryLabel(v),
    },
    ...asColumns<ChannelRow>(metricColumns),
  ];

  const countryColumns: ColumnsType<MerchantCountryRow> = [
    { title: "序号", dataIndex: "index", width: 64, align: "center" },
    { title: "商户名称", dataIndex: "merchantName", ellipsis: true },
    { title: "商户ID", dataIndex: "merchantId", ellipsis: true },
    {
      title: "国家/地区",
      dataIndex: "country",
      render: (v: string) => countryLabel(v),
    },
    ...asColumns<MerchantCountryRow>(metricColumns),
  ];

  const channelTotals = channelStats?.totals ?? emptyMetrics();
  const countryTotals = countryStats?.totals ?? emptyMetrics();

  return (
    <div className="home-page">
      <Typography.Title level={4} className="home-page__title">
        首页
      </Typography.Title>

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      <Spin spinning={loading}>
        <Card className="home-page__card" bordered={false}>
          <Tabs
            defaultActiveKey="channel"
            items={[
              {
                key: "channel",
                label: "支付平台各国家渠道流水",
                children: (
                  <Table<ChannelRow>
                    size="middle"
                    columns={channelColumns}
                    dataSource={channelRows}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 960 }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            合计
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            {channelTotals.lastMonthTotalUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            {channelTotals.lastMonthToYesterdayUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            {channelTotals.mtdToYesterdayUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">
                            {channelTotals.momDiffRatio}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            {channelTotals.projectedMomDiffRatio}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                ),
              },
              {
                key: "country",
                label: "支付平台各国家商户流水",
                children: (
                  <Table<MerchantCountryRow>
                    size="middle"
                    columns={countryColumns}
                    dataSource={countryRows}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: 1100 }}
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={4}>
                            合计
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            {countryTotals.lastMonthTotalUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            {countryTotals.lastMonthToYesterdayUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">
                            {countryTotals.mtdToYesterdayUsd}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            {countryTotals.momDiffRatio}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8} align="right">
                            {countryTotals.projectedMomDiffRatio}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                ),
              },
            ]}
          />
        </Card>

        <Card className="home-page__card home-page__cashflow" bordered={false}>
          <div className="home-page__cashflow-head">
            <Typography.Title level={5} className="home-page__section-title">
              支付平台过去30天各金流的交易量
            </Typography.Title>
            <div className="home-page__kpi-row">
              <div className="home-page__kpi">
                <EnvironmentOutlined className="home-page__kpi-icon" />
                <div>
                  <div className="home-page__kpi-value">{cashflow?.totalOrders ?? 0}</div>
                  <div className="home-page__kpi-label">总订单数</div>
                </div>
              </div>
              <div className="home-page__kpi">
                <TeamOutlined className="home-page__kpi-icon" />
                <div>
                  <div className="home-page__kpi-value">{cashflow?.totalUsd ?? "0.00"}</div>
                  <div className="home-page__kpi-label">充值总额</div>
                </div>
              </div>
            </div>
          </div>

          <CashflowLineChart series={cashflow?.series ?? []} />

          <div className="home-page__cashflow-foot">
            <Radio.Group value="orders">
              <Radio value="orders">订单数据</Radio>
            </Radio.Group>
          </div>
        </Card>
      </Spin>
    </div>
  );
}
